// public/trade-logic.js
// Pure functions for trade status logic - no side effects, easily testable

/**
 * Card status constants
 */
export const CARD_STATUS = {
  TRADE: 'trade',
  CONSIDERING: 'considering',
  SUGGESTED: 'suggested',
};

/**
 * Gets the trade status of a card if it's in the current trade
 * @param {Object} cardInfo - The card info object with cardData and isFoil
 * @param {Object} tradeState - Object with mode, currentTrade, viewingSide
 * @returns {string|null} - The status ('trade', 'considering', 'suggested') or null if not in trade
 */
export function getCardTradeStatus(cardInfo, tradeState) {
  const { mode, currentTrade, viewingSide } = tradeState;

  if (mode !== 'trade' || !currentTrade || !viewingSide) {
    return null;
  }

  const { cardData, isFoil } = cardInfo;
  if (!cardData) return null;

  const sideKey = `side${viewingSide}`;
  const sideCards = currentTrade[sideKey]?.cards || [];

  const tradeCard = sideCards.find(
    (c) =>
      c.scryfallId === cardData.id &&
      c.set === cardData.set &&
      c.collectorNumber?.toLowerCase() === cardData.collector_number?.toLowerCase() &&
      c.isFoil === isFoil
  );

  if (tradeCard) {
    return tradeCard.status || CARD_STATUS.TRADE; // Default for legacy cards
  }

  return null;
}

/**
 * Determines if the user can cycle trade statuses (viewing other user's binder)
 * @param {string} userSide - The user's side ('A' or 'B')
 * @param {string} viewingSide - The side being viewed ('A' or 'B')
 * @param {string} mode - Current mode ('trade' or 'normal')
 * @returns {boolean}
 */
export function canCycleTradeStatus(userSide, viewingSide, mode) {
  return mode === 'trade' && userSide !== viewingSide;
}

/**
 * Gets the next status in the trade cycle (when viewing other user's binder)
 * Cycle: suggested -> trade -> considering -> null (remove)
 * @param {string} currentStatus - Current trade status
 * @returns {string|null} - Next status, or null if card should be removed
 */
export function getNextTradeStatus(currentStatus) {
  switch (currentStatus) {
    case CARD_STATUS.SUGGESTED:
      return CARD_STATUS.TRADE;
    case CARD_STATUS.TRADE:
      return CARD_STATUS.CONSIDERING;
    case CARD_STATUS.CONSIDERING:
      return null; // Remove from trade
    default:
      return null;
  }
}

/**
 * Gets the toggled status for own binder cards (trade <-> considering)
 * When viewing your own binder, clicking on a trade/considering card swaps between those two states
 * @param {string} currentStatus - Current trade status
 * @returns {string|null} - Toggled status, or null if not applicable
 */
export function getToggledOwnBinderStatus(currentStatus) {
  switch (currentStatus) {
    case CARD_STATUS.TRADE:
      return CARD_STATUS.CONSIDERING;
    case CARD_STATUS.CONSIDERING:
      return CARD_STATUS.TRADE;
    default:
      return null; // suggested cards or unknown status don't toggle
  }
}

/**
 * Creates a card identifier object for trade operations
 * @param {Object} cardInfo - The card info object
 * @returns {Object} - Card identifier with scryfallId, set, collectorNumber, isFoil
 */
export function createCardIdentifier(cardInfo) {
  const { cardData, isFoil } = cardInfo;
  return {
    scryfallId: cardData.id,
    set: cardData.set,
    collectorNumber: cardData.collector_number,
    isFoil: isFoil,
  };
}

/**
 * Determines the action to take when clicking on a selected card's overlay
 * @param {Object} cardInfo - The card info object
 * @param {Object} tradeState - Object with mode, currentTrade, viewingSide, userSide
 * @returns {Object} - Action object with type and optional data
 *   - { type: 'cycle', nextStatus: string, cardIdentifier: Object } - Cycle to next status
 *   - { type: 'remove' } - Remove card from trade (deselect)
 *   - { type: 'deselect' } - Simple deselect (not in trade mode)
 */
export function getSelectionOverlayAction(cardInfo, tradeState) {
  const { mode, userSide, viewingSide } = tradeState;

  // Check if we can cycle trade statuses (viewing other user's binder)
  if (canCycleTradeStatus(userSide, viewingSide, mode)) {
    const tradeStatus = getCardTradeStatus(cardInfo, tradeState);

    if (tradeStatus) {
      const nextStatus = getNextTradeStatus(tradeStatus);

      if (nextStatus === null) {
        // Considering -> Remove
        return { type: 'remove' };
      }

      return {
        type: 'cycle',
        nextStatus,
        cardIdentifier: createCardIdentifier(cardInfo),
      };
    }
  }

  // Viewing own binder in trade mode - toggle between trade/considering
  if (mode === 'trade' && userSide === viewingSide) {
    const tradeStatus = getCardTradeStatus(cardInfo, tradeState);

    if (tradeStatus) {
      const toggledStatus = getToggledOwnBinderStatus(tradeStatus);

      if (toggledStatus) {
        return {
          type: 'cycle',
          nextStatus: toggledStatus,
          cardIdentifier: createCardIdentifier(cardInfo),
        };
      }
    }
  }

  // Not in trade mode or no trade status - just deselect
  return { type: 'deselect' };
}
